import * as Sentry from "@sentry/react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useRegisterSW } from "virtual:pwa-register/react"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { SessionStorageService } from "~Shared/services/sessionstorage.service"

const UPDATE_CHECK_INTERVAL_MS = 1000 * 60
// updateServiceWorker() reloads once the new worker controls the page; a page that is not
// controlled by a service worker never gets that event, so reload it ourselves
const RELOAD_FALLBACK_MS = 1000 * 10
// WebKit needs a moment to restart a worker it stopped, so keep nudging for ~2s before giving up
const SKIP_WAITING_RETRY_MS = 250
const SKIP_WAITING_RETRY_LIMIT = 8
// a reload discards whatever Sentry has not sent yet, so give the transport a moment first
const SENTRY_FLUSH_MS = 1000 * 2
const RUNNING_VERSION = `${PROCESS_ENV.NPM_PACKAGE_VERSION}-${PROCESS_ENV.CI_COMMIT}`

const determineUpdateTimeInSec = () => {
    switch (window.location.pathname) {
        case RoutePathEnum.ROOT:
        case RoutePathEnum.LOGIN:
            return 5
        default:
            return 30
    }
}

// updates that do not apply cleanly are invisible without this: they leave no error behind, just a
// user stuck on an old build or a page that keeps reloading
type ServiceWorkerUpdateReport = {
    message: string
    level: Sentry.SeverityLevel
    registration: ServiceWorkerRegistration | undefined
}

const reportServiceWorkerUpdate = ({ message, level, registration }: ServiceWorkerUpdateReport) =>
    Sentry.captureMessage(`[SW] ${message}`, {
        level,
        tags: { errorType: "SW_UPDATE" },
        extra: {
            version: RUNNING_VERSION,
            isPageControlled: !!navigator.serviceWorker.controller,
            waitingWorkerState: registration?.waiting?.state,
            installingWorkerState: registration?.installing?.state,
            activeWorkerState: registration?.active?.state,
            pathname: window.location.pathname,
        },
    })

const reloadAfterFlushingSentry = () => void Sentry.flush(SENTRY_FLUSH_MS).finally(() => window.location.reload())

type SkipWaitingRetry = {
    requestSkipWaiting: () => void
    getRegistration: () => ServiceWorkerRegistration | undefined
}

// a single SKIP_WAITING message is dropped whenever Safari has already stopped the waiting worker.
// re-sending it gives WebKit a chance to restart the worker and pick one up; a worker that leaves
// the waiting state is the update taking over, which is where we stop
const retrySkipWaitingWhileUpdateIsWaiting = ({ requestSkipWaiting, getRegistration }: SkipWaitingRetry) => {
    let attempts = 0

    const retryIntervalId = setInterval(() => {
        attempts += 1

        if (!getRegistration()?.waiting || attempts > SKIP_WAITING_RETRY_LIMIT) {
            clearInterval(retryIntervalId)
            return
        }

        console.debug(`[SW] Update is still waiting, re-sending skip waiting: attempt=${attempts}`)
        requestSkipWaiting()
    }, SKIP_WAITING_RETRY_MS)

    return retryIntervalId
}

// when even the retries above are dropped the update never activates. Reloading then re-serves the
// precached shell of the running version, which finds the same worker still waiting and schedules
// the next reload — a loop. Only reload when the new version can actually take over: either nothing
// controls this page (the event that would reload it never fires) or no worker is waiting anymore
// (the update is already in charge).
const reloadIfNewVersionCanTakeOver = (registration: ServiceWorkerRegistration | undefined) => {
    const isPageControlled = !!navigator.serviceWorker.controller

    if (isPageControlled && registration?.waiting) {
        console.debug(`[SW] Update is still waiting, skipping reload fallback`)
        reportServiceWorkerUpdate({ message: "Update never activated, skipping reload fallback", level: "warning", registration })
        return
    }

    if (SessionStorageService.hasAttemptedReloadFallback(RUNNING_VERSION)) {
        console.debug(`[SW] Reload fallback already attempted for version ${RUNNING_VERSION}`)
        reportServiceWorkerUpdate({ message: "Reload fallback already spent, update is not taking effect", level: "warning", registration })
        return
    }

    SessionStorageService.markReloadFallbackAttempted(RUNNING_VERSION)
    // an uncontrolled page is the case this fallback was built for; a controlled one means the
    // update activated but never reloaded us, which it should have
    reportServiceWorkerUpdate({
        message: "Reloading to pick up the new version",
        level: isPageControlled ? "warning" : "info",
        registration,
    })
    reloadAfterFlushingSentry()
}

export const useAutoUpdate = () => {
    // show message to user if new version is available and schedule auto-reload
    const { t } = useTranslation()
    const registrationRef = useRef<ServiceWorkerRegistration>()
    const updateCheckIntervalRef = useRef<ReturnType<typeof setInterval>>()

    const {
        needRefresh: [needRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        // without this the plugin reloads on its own once the new worker takes control, unobserved.
        // that is the one reload we never see, and the one that can still loop: a deploy serving two
        // builds side by side hands out a genuinely new worker on every update check.
        onNeedReload: () => {
            console.debug(`[SW] New version took control, reloading`)
            reportServiceWorkerUpdate({
                message: "New version took control, reloading",
                level: "info",
                registration: registrationRef.current,
            })
            reloadAfterFlushingSentry()
        },
        onRegisteredSW: (swUrl, registration) => {
            console.debug(`[SW] Registered serviceWorker with url ${swUrl} version ${RUNNING_VERSION}`)
            if (!registration) {
                return
            }

            registrationRef.current = registration
            updateCheckIntervalRef.current = setInterval(() => {
                console.debug(`[SW] Checking for updates`)
                void registration.update()
            }, UPDATE_CHECK_INTERVAL_MS)
        },
        onRegisterError: (error: unknown) => {
            console.error(`[SW] Error during service worker registration: ${error}`)
            Sentry.captureException(error, { tags: { errorType: "SW_REGISTRATION" }, extra: { version: RUNNING_VERSION } })
        },
    })

    useEffect(() => {
        return () => clearInterval(updateCheckIntervalRef.current)
    }, [])

    useEffect(() => {
        if (!needRefresh) {
            return
        }

        const updateTimeInSec = determineUpdateTimeInSec()
        console.debug(`[SW] Update available, reloading in ${updateTimeInSec}s`)

        NotificationService.send(
            NotificationTypeEnum.INFO,
            t("notification:new_version.title"),
            t("notification:new_version.message", { time: updateTimeInSec }),
            updateTimeInSec * 1000,
        )

        let reloadFallbackTimeoutId: ReturnType<typeof setTimeout>
        let skipWaitingRetryIntervalId: ReturnType<typeof setInterval>

        const updateTimeoutId = setTimeout(() => {
            void updateServiceWorker()
            skipWaitingRetryIntervalId = retrySkipWaitingWhileUpdateIsWaiting({
                requestSkipWaiting: () => void updateServiceWorker(),
                getRegistration: () => registrationRef.current,
            })
            reloadFallbackTimeoutId = setTimeout(() => reloadIfNewVersionCanTakeOver(registrationRef.current), RELOAD_FALLBACK_MS)
        }, updateTimeInSec * 1000)

        return () => {
            clearTimeout(updateTimeoutId)
            clearTimeout(reloadFallbackTimeoutId)
            clearInterval(skipWaitingRetryIntervalId)
        }
    }, [needRefresh, updateServiceWorker, t])
}
